/**
 * ClientSwitcher — sidebar control that lists the actor's visible clients.
 *
 * Per `docs/frontend/035-frontend-app-shell-plan.md §3.4`:
 *   - non-leadership: sources from `me.client_assignments` (no extra call).
 *   - leadership: sources from `list-clients` (org-wide).
 *
 * Leadership/client-workspace selection preserves the current client tab.
 * Production staff selection returns to their role workspace with a client
 * query filter, so selecting a client never opens its administration overview.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useClientScope } from '../../context/ClientScopeProvider';
import { useMeContext } from '../../context/MeContextProvider';
import { isAgencyProductionOnly } from '../../permissions';
import { CaretDownIcon, SearchIcon } from './icons';

interface ClientSwitcherProps {
  readonly activeClientId: string | null;
}

function clientInitials(name: string): string {
  const parts = name
    .split(/\s+/)
    .filter((p) => p.length > 0)
    .slice(0, 2);
  if (parts.length === 0) return name.slice(0, 2);
  return parts.map((p) => p.charAt(0)).join('.');
}

export function ClientSwitcher({ activeClientId }: ClientSwitcherProps) {
  // Task 287B — role-aware wording: the provider's `leadership` flag separates the
  // org-wide view (owner/marketing_manager see every client) from the assignment
  // view (everyone else sees ONLY their assigned clients). The old copy showed the
  // agency-wide leadership wording to everyone, falsely implying org-wide access
  // for assignment-scoped staff.
  const { clients, status, leadership } = useClientScope();
  const { me } = useMeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const active = activeClientId ? clients.find((c) => c.id === activeClientId) : null;
  const productionWorkspace = me ? isAgencyProductionOnly(me) : false;

  const onPick = useCallback(
    (clientId: string) => {
      setOpen(false);
      if (productionWorkspace) {
        navigate(`/app?client=${encodeURIComponent(clientId)}`);
        return;
      }
      // Preserve the current tab when switching between clients.
      const m = location.pathname.match(/^\/app\/clients\/[^/]+(\/[^/]+)?/);
      const tab = m && m[1] ? m[1] : '/overview';
      navigate(`/app/clients/${clientId}${tab}`);
    },
    [location.pathname, navigate, productionWorkspace],
  );

  const filtered =
    filter.trim().length > 0
      ? clients.filter((c) => c.name.toLowerCase().includes(filter.trim().toLowerCase()))
      : clients;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="switcher"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="sw-thumb">{active ? clientInitials(active.name) : 'ك.و'}</span>
        <span className="sw-info">
          <span className="sw-label">
            {active ? 'عميل نشط' : leadership ? 'رؤية الوكالة' : 'وصول حسب التعيين'}
          </span>
          <span className="sw-name">
            {active
              ? active.name
              : leadership
                ? `جميع العملاء (${clients.length})`
                : `عملاؤك المعيّنون (${clients.length})`}
          </span>
        </span>
        <CaretDownIcon className="sw-caret" />
      </button>
      {open ? (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            insetInlineStart: 0,
            insetInlineEnd: 0,
            top: '100%',
            marginTop: 6,
            background: 'var(--paper)',
            border: '1px solid var(--line-2)',
            borderRadius: 'var(--r-md)',
            boxShadow: 'var(--sh-pop)',
            padding: 8,
            zIndex: 'var(--z-drawer)',
            maxHeight: 360,
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              padding: 6,
              borderBottom: '1px solid var(--line)',
              marginBottom: 6,
              position: 'relative',
            }}
          >
            <input
              type="text"
              placeholder="ابحث عن عميل…"
              value={filter}
              onChange={(e) => setFilter(e.currentTarget.value)}
              style={{
                width: '100%',
                border: '1px solid var(--line-2)',
                background: 'var(--paper-2)',
                borderRadius: 'var(--r-sm)',
                padding: '7px 30px 7px 10px',
                fontSize: 12,
                fontFamily: 'inherit',
              }}
              autoFocus
            />
            <SearchIcon className="" />
          </div>
          {status === 'loading' ? (
            <div className="tiny" style={{ padding: 10 }}>
              جاري تحميل قائمة العملاء…
            </div>
          ) : filtered.length === 0 ? (
            <div className="tiny" style={{ padding: 10 }}>
              لا يوجد عميل مطابق.
            </div>
          ) : (
            filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                role="option"
                aria-selected={c.id === activeClientId}
                onClick={() => onPick(c.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  borderRadius: 'var(--r-sm)',
                  width: '100%',
                  background: c.id === activeClientId ? 'var(--cream-2)' : 'transparent',
                  cursor: 'pointer',
                  textAlign: 'start',
                  fontSize: 13,
                }}
              >
                <span
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 6,
                    background:
                      c.id === activeClientId ? 'var(--tangerine-soft)' : 'var(--cream-2)',
                    color: c.id === activeClientId ? 'var(--tangerine-ink)' : 'var(--ink-2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'var(--font-serif)',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {clientInitials(c.name)}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{c.name}</span>
                  <span className="tiny" style={{ display: 'block', color: 'var(--muted)' }}>
                    {c.status}
                  </span>
                </span>
              </button>
            ))
          )}
          <div style={{ borderTop: '1px solid var(--line)', marginTop: 6, padding: 6 }}>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/app/clients');
              }}
              className="btn btn-ghost btn-sm"
              style={{ width: '100%' }}
            >
              {leadership ? 'عرض كل العملاء' : 'عرض عملائك المعيّنين'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
